export interface CanonicalEmailSendResult {
  operation?: {
    status?: string;
  };
  message?: {
    id?: number | string;
    templateReplyState?: 'pending' | 'replied' | null;
  };
  delivery?: {
    accepted: string[];
    rejected: string[];
    partial: boolean | null;
    observed?: boolean;
  };
}

/** SMTP acceptance is not a receipt from the recipient's mailbox. */
export function canonicalEmailSendFeedback(result: CanonicalEmailSendResult): {
  title: string;
  description: string;
  variant: 'default' | 'destructive';
  duration: number;
} {
  const accepted = result.delivery?.accepted ?? [];
  const rejected = result.delivery?.rejected ?? [];
  if (!result.delivery || result.delivery.observed === false || result.delivery.partial === null
    || (accepted.length === 0 && rejected.length === 0)) {
    return {
      title: 'Invio registrato: esito destinatari non disponibile',
      description: "Verifica la conversazione prima di un nuovo invio: l'esito del server per i singoli destinatari non è disponibile.",
      variant: 'default',
      duration: 15000,
    };
  }
  const partial = result.delivery?.partial === true || rejected.length > 0;
  if (partial) {
    return {
      title: 'Invio parziale: destinatari rifiutati',
      description: [
        accepted.length ? `Accettata dal server di invio per: ${accepted.join(', ')}.` : 'Il server ha registrato un invio parziale.',
        rejected.length ? `Rifiutata dal server per: ${rejected.join(', ')}.` : 'Verifica i destinatari rifiutati nella conversazione.',
        'Non reinviare a tutti: controlla e correggi soltanto gli indirizzi rifiutati.',
      ].join(' '),
      variant: 'destructive',
      duration: 15000,
    };
  }
  return {
    title: 'Email accettata dal server di invio',
    description: accepted.length
      ? `Accettata dal server per: ${accepted.join(', ')}.`
      : "Il server ha confermato l'invio del messaggio.",
    variant: 'default',
    duration: 5000,
  };
}

export function uncertainEmailSendFeedback(error: unknown) {
  if (typeof error !== 'object' || error === null || !('code' in error)
    || error.code !== 'smtp_outcome_unknown') return undefined;
  return {
    title: 'Esito invio da verificare',
    description: 'Il server potrebbe aver già accettato la mail. Controlla la cartella Inviate prima di riprovare.',
    variant: 'destructive' as const,
    duration: 15000,
  };
}

export type EmailCrmAssociationEntityType = 'contact' | 'company' | 'branch' | 'lead' | 'deal';

export interface EmailCrmAssociationInput {
  entityType: EmailCrmAssociationEntityType;
  entityId: number | string | null | undefined;
}

export interface EmailCrmAssociation {
  id: number;
  scope: 'message' | 'thread';
  messageId?: number | null;
  threadId?: number | null;
  entityType: EmailCrmAssociationEntityType | string;
  entityId: number;
  source?: string | null;
  status?: string | null;
  entityStatus?: string | null;
  inheritedFromAssociationId?: number | null;
  /** Nome leggibile del record collegato, risolto dal server. Vedi CRM_ASSOCIATIONS_LATERAL_SQL. */
  entityName?: string | null;
}

export interface PropagateEmailCrmAssociationsResult {
  associations: EmailCrmAssociation[];
  blocked: Array<{
    messageAssociationId: number;
    entityType: string;
    entityId: number;
  }>;
}

export interface RemoveEmailThreadAssociationResult {
  association: EmailCrmAssociation;
  exclusion: {
    id: number;
    scope: 'thread';
    threadId: number;
    entityType: string;
    entityId: number;
    reason?: string | null;
    status: string;
  };
}

export interface RemoveEmailMessageAssociationResult {
  association: EmailCrmAssociation;
  exclusion: {
    id: number;
    scope: 'message';
    messageId: number;
    entityType: string;
    entityId: number;
    reason?: string | null;
    status: string;
  };
}

export interface ManualEmailReceiveExpectedInput {
  subjectContains?: string;
  from?: string;
  rfcMessageId?: string;
}

export interface ManualEmailReceiveFormInput extends ManualEmailReceiveExpectedInput {
  maxMessages?: number | string;
  recentMinutes?: number | string;
}

export interface ManualEmailReceivePayload {
  expected?: ManualEmailReceiveExpectedInput;
  maxMessages: number;
  recentMinutes: number;
}

export interface ManualEmailReceiveMessage {
  id: number;
  threadId: number | null;
  providerUid: number | null;
  rfcMessageId: string | null;
  subject: string | null;
  providerDate: string | null;
  receivedAt: string | null;
  created: boolean;
}

export interface ManualEmailReceiveResult {
  mode: 'manual_limited_receive';
  accountId: number;
  folderId: number;
  scannedUidRange: { fromUid: number; toUid: number } | null;
  fetched: number;
  matched: number;
  imported: number;
  existing: number;
  syncCheckpointAdvanced: false;
  messages: ManualEmailReceiveMessage[];
}

export interface CanonicalSendAndLinkInput {
  endpoint?: string;
  payload: Record<string, unknown>;
  associations?: EmailCrmAssociationInput[];
}

export interface CanonicalSendAndLinkOptions {
  onAssociationError?: (error: unknown) => void;
}

export function createEmailSendIdempotencyKey(scope = 'compose'): string {
  const randomId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${scope}-${randomId}`;
}

export function splitEmailRecipients(value?: string): string[] {
  return (value || '')
    .split(',')
    .map((email) => email.trim())
    .filter(Boolean);
}

export function plainTextToHtml(value: string): string {
  return value.replace(/\n/g, '<br>');
}

export function assertCanonicalSendSucceeded(result: CanonicalEmailSendResult): void {
  if (!result?.message?.id || result?.operation?.status !== 'sent') {
    throw new Error('Invio email non confermato dal server');
  }
}

function trimOptional(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

function parseManualReceiveLimit(value: number | string | undefined, fallback: number, name: string, max: number): number {
  const parsed = value === undefined || value === '' ? fallback : Number(value);
  if (!Number.isSafeInteger(parsed) || parsed <= 0 || parsed > max) {
    throw new Error(`${name} deve essere compreso tra 1 e ${max}`);
  }
  return parsed;
}

export function buildManualEmailReceivePayload(input: ManualEmailReceiveFormInput): ManualEmailReceivePayload {
  const expected = {
    subjectContains: trimOptional(input.subjectContains),
    from: trimOptional(input.from),
    rfcMessageId: trimOptional(input.rfcMessageId),
  };
  const expectedPayload = Object.fromEntries(
    Object.entries(expected).filter(([, value]) => Boolean(value)),
  ) as ManualEmailReceiveExpectedInput;
  const maxMessages = parseManualReceiveLimit(input.maxMessages, 3, 'maxMessages', 10);
  const recentMinutes = parseManualReceiveLimit(input.recentMinutes, 10, 'recentMinutes', 15);

  return {
    ...(Object.keys(expectedPayload).length > 0 ? { expected: expectedPayload } : {}),
    maxMessages,
    recentMinutes,
  };
}

export function normalizeEmailCrmAssociations(
  associations: EmailCrmAssociationInput[],
): Array<{ entityType: EmailCrmAssociationEntityType; entityId: number }> {
  const seen = new Set<string>();
  const normalized: Array<{ entityType: EmailCrmAssociationEntityType; entityId: number }> = [];

  for (const association of associations) {
    const entityId = Number(association.entityId);
    if (!Number.isSafeInteger(entityId) || entityId <= 0) continue;
    const key = `${association.entityType}:${entityId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push({ entityType: association.entityType, entityId });
  }

  return normalized;
}

export async function linkEmailCrmAssociationsAfterSend(
  sendResult: CanonicalEmailSendResult,
  associations: EmailCrmAssociationInput[],
  request: (method: string, url: string, data?: unknown) => Promise<unknown>,
): Promise<void> {
  assertCanonicalSendSucceeded(sendResult);
  const normalized = normalizeEmailCrmAssociations(associations);
  if (normalized.length === 0) return;

  await request('POST', `/api/email/messages/${sendResult.message!.id}/crm-associations`, {
    associations: normalized,
  });
}

export async function sendCanonicalEmailThenLinkCrmAssociations(
  input: CanonicalSendAndLinkInput,
  request: (method: string, url: string, data?: unknown) => Promise<unknown>,
  options: CanonicalSendAndLinkOptions = {},
): Promise<CanonicalEmailSendResult> {
  const result = await request('POST', input.endpoint || '/api/email/send', input.payload) as CanonicalEmailSendResult;
  assertCanonicalSendSucceeded(result);
  globalThis.setTimeout(() => {
    void linkEmailCrmAssociationsAfterSend(result, input.associations || [], request)
      .catch((error) => {
        if (options.onAssociationError) {
          options.onAssociationError(error);
          return;
        }
        console.error('[email] collegamenti CRM post-invio non completati', error);
      });
  }, 0);
  return result;
}

export async function promoteEmailMessageAssociationsToThread(
  messageId: number | string,
  associationIds: Array<number | string>,
  request: (method: string, url: string, data?: unknown) => Promise<unknown>,
): Promise<PropagateEmailCrmAssociationsResult> {
  const normalizedMessageId = Number(messageId);
  const normalizedAssociationIds = associationIds
    .map((id) => Number(id))
    .filter((id) => Number.isSafeInteger(id) && id > 0);

  if (!Number.isSafeInteger(normalizedMessageId) || normalizedMessageId <= 0) {
    throw new Error('ID messaggio email non valido');
  }
  if (normalizedAssociationIds.length === 0) {
    throw new Error('Nessuna associazione messaggio da promuovere');
  }

  return request(
    'POST',
    `/api/email/messages/${normalizedMessageId}/crm-associations/propagate-to-thread`,
    { associationIds: normalizedAssociationIds },
  ) as Promise<PropagateEmailCrmAssociationsResult>;
}

export async function removeEmailThreadAssociationWithExclusion(
  threadId: number | string,
  associationId: number | string,
  reason: string,
  request: (method: string, url: string, data?: unknown) => Promise<unknown>,
): Promise<RemoveEmailThreadAssociationResult> {
  const normalizedThreadId = Number(threadId);
  const normalizedAssociationId = Number(associationId);
  const normalizedReason = reason.trim();

  if (!Number.isSafeInteger(normalizedThreadId) || normalizedThreadId <= 0) {
    throw new Error('ID thread email non valido');
  }
  if (!Number.isSafeInteger(normalizedAssociationId) || normalizedAssociationId <= 0) {
    throw new Error('ID associazione email non valido');
  }
  if (!normalizedReason) {
    throw new Error('Motivo rimozione associazione richiesto');
  }

  return request(
    'POST',
    `/api/email/threads/${normalizedThreadId}/crm-associations/${normalizedAssociationId}/remove`,
    { reason: normalizedReason },
  ) as Promise<RemoveEmailThreadAssociationResult>;
}

export async function removeEmailMessageAssociationWithExclusion(
  messageId: number | string,
  associationId: number | string,
  reason: string,
  request: (method: string, url: string, data?: unknown) => Promise<unknown>,
): Promise<RemoveEmailMessageAssociationResult> {
  const normalizedMessageId = Number(messageId);
  const normalizedAssociationId = Number(associationId);
  const normalizedReason = reason.trim();

  if (!Number.isSafeInteger(normalizedMessageId) || normalizedMessageId <= 0) {
    throw new Error('ID messaggio email non valido');
  }
  if (!Number.isSafeInteger(normalizedAssociationId) || normalizedAssociationId <= 0) {
    throw new Error('ID associazione email non valido');
  }
  if (!normalizedReason) {
    throw new Error('Motivo rimozione associazione richiesto');
  }

  return request(
    'POST',
    `/api/email/messages/${normalizedMessageId}/crm-associations/${normalizedAssociationId}/remove`,
    { reason: normalizedReason },
  ) as Promise<RemoveEmailMessageAssociationResult>;
}

export function mergePromotedThreadAssociations(
  current: EmailCrmAssociation[],
  result: PropagateEmailCrmAssociationsResult,
): EmailCrmAssociation[] {
  const byId = new Map(current.map((association) => [association.id, association]));
  for (const association of result.associations) {
    byId.set(association.id, association);
  }
  return [...byId.values()];
}

export function mergeRemovedThreadAssociation(
  current: EmailCrmAssociation[],
  result: RemoveEmailThreadAssociationResult,
): EmailCrmAssociation[] {
  return current.map((association) => (
    association.id === result.association.id ? { ...association, ...result.association } : association
  ));
}

export function mergeRemovedEmailAssociation(
  current: EmailCrmAssociation[],
  result: RemoveEmailThreadAssociationResult | RemoveEmailMessageAssociationResult,
): EmailCrmAssociation[] {
  return current.map((association) => (
    association.id === result.association.id ? { ...association, ...result.association } : association
  ));
}
